import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";

const TYPE_LABEL = {
  employee: "Employee",
  payroll_run: "Payroll Run",
  payslip: "Payslip",
  leave: "Leave",
  overtime: "Overtime",
  candidate: "Candidate",
  ticket: "Ticket",
  document: "Document",
  announcement: "Announcement",
};

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!q.trim()) {
      setItems([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/operations/search", { params: { q, limit: 8 } });
        setItems(data.items || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (it) => {
    setOpen(false);
    setQ("");
    setItems([]);
    if (it.route) navigate(it.route);
  };

  return (
    <div ref={rootRef} className="flex-1 max-w-xl global-search relative">
      <div className="relative">
        <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          placeholder="Search employees, payslips, tickets, candidates…"
          className="w-full h-9 pl-9 pr-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          data-testid="global-search"
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        />
      </div>
      {open && (q.trim() || items.length > 0) && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-background shadow-lg max-h-96 overflow-auto">
          {loading && <div className="p-3 text-xs text-muted-foreground">Searching…</div>}
          {!loading && items.length === 0 && (
            <div className="p-3 text-xs text-muted-foreground">No results</div>
          )}
          {items.map((it, idx) => (
            <button
              key={`${it.type}-${it.id}-${idx}`}
              onClick={() => go(it)}
              className="w-full text-left px-3 py-2 hover:bg-muted border-b last:border-0"
            >
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {TYPE_LABEL[it.type] || it.type}
              </div>
              <div className="text-sm font-medium">{it.title}</div>
              {it.subtitle && (
                <div className="text-xs text-muted-foreground">{it.subtitle}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
